/**
 * Phone number and email formatting/validation utilities
 */

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email format
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone format (must be 10 digits for US)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid phone format for SMS
 */
export const isValidPhone = (phone) => {
  if (!phone) return false;
  const digits = phone.replace(/\D/g, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
};

/**
 * Format phone number for display (human-readable format)
 * Converts E.164 format (+15551234567) to (555) 123-4567
 * @param {string} phone - Phone number in any format
 * @returns {string} Formatted phone number for display
 */
export const formatPhoneDisplay = (phone) => {
  if (!phone) return '';

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Handle US numbers (10 or 11 digits)
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  // Return original for international or other formats
  return phone;
};

/**
 * Format phone number to E.164 format for storage/SMS
 * @param {string} phone - Phone number in any format
 * @returns {string} Phone number in E.164 format (+15551234567)
 */
export const formatPhoneE164 = (phone) => {
  if (!phone) return '';

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Already has country code prefix
  if (phone.startsWith('+') && digits.length >= 10) {
    return '+' + digits;
  }

  // US number: 10 digits
  if (digits.length === 10) {
    return '+1' + digits;
  }

  // US number: 11 digits starting with 1
  if (digits.length === 11 && digits.startsWith('1')) {
    return '+' + digits;
  }

  // Return original if can't normalize
  return phone;
};

/**
 * Format phone input as user types (auto-format)
 * @param {string} value - Current input value
 * @returns {string} Formatted value for input display
 */
export const formatPhoneInput = (value) => {
  if (!value) return '';

  // Remove all non-digit characters, and the country code: no US area code
  // starts with 1, so "+1 407.555.0142" or a stored "+14075550142" is
  // (407) 555-0142, never (140) 755-5014.
  let digits = value.replace(/\D/g, '');
  if (digits.startsWith('1') && (digits.length > 10 || /^\s*\+\s*1/.test(value))) digits = digits.slice(1);

  // Format as user types
  if (digits.length <= 3) {
    return digits;
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  if (digits.length <= 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  // Max 10 digits for US numbers
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};
